import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import {useAtom, useSetAtom} from 'jotai';
import {categoriesAtom, selectedDepartmentsAtom, matchingClientsAtom} from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import ValueBar from "../../components/Bars/ValueBar.tsx";
import RiskBadge from "../../components/Badges/RiskBadge.tsx";
import PercentBar from "../../components/Bars/PercentBar.tsx";
import {ChevronDown} from "flowbite-react-icons/outline";
import {Scatter} from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from 'chart.js';


ChartJS.register(CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

interface QuestionInterface {
  id: string;
  question: string;
  questionId: string;
  scoreSelf: number;
  scoreOther: number;
}

interface SurveySection {
  id: string;
  name: string;
  scoreSelf: number;
  scoreOther: number;
  clientsResponded: number;
  questions: Array<QuestionInterface>;
  scatter: Array<any>;
}

interface AssessmentInterface {
  scoreSelf: number;
  scoreOther: number;
  sections: Array<SurveySection>;
}

interface SurveyInterface {
  id: string;
  answerOptions: number;
  name: string;
  assessment: AssessmentInterface;
}

export default function StrategicInitiative() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [surveys, setSurveys] = useState<Array<SurveyInterface>>([]);
  const [categories] = useAtom(categoriesAtom);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [openedSections, setOpenedSections] = useState<Array<string>>([]);

  useEffect(() => {
    getData();
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_initiative', data).then((response) => {
      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients)
      } else {
        setMatchingClients(response.json.data.matchingClients);
        setSurveys(response.json.data.surveys)
        setTooFewClients(undefined)
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  const renderSurvey = (survey: SurveyInterface) => {
    return (
      <div key={"survey_" + survey.id} className="mt-4">
        {renderAssessment(survey)}
      </div>
    )
  }

  const toggleSection = (sectionId: string, isOpened: boolean)  => {
    if (!isOpened) {
      setOpenedSections([...openedSections, sectionId]);
    } else {
      const _openedSections: Array<string> = [];
      for (const _sectionId of openedSections) {
        if (sectionId !== _sectionId) {
          _openedSections.push(_sectionId);
        }
      }
      setOpenedSections(_openedSections);
    }
  }

  const getScoreColor = (score: number, max: number) => {
    let zoneLimits = [3, 6];
    if (max === 5) {
      zoneLimits = [3, 4];
    }
    if (score < zoneLimits[0]) {
      return "cherry-600";
    } else if (score < zoneLimits[1]) {
      return "orange-500";
    } else {
      return "aquamarine-600";
    }
  }

  const renderSpread = (section: SurveySection, answerOptions: number) => {
    const datasets = [
      {
        pointRadius: 3,
        pointBackgroundColor: "rgba(255,128,0,1)",
        data: [],
      },
      {
        pointRadius: 3,
        pointBackgroundColor: "rgba(128,128,128,1)",
        data: [],
      },
      {
        pointRadius: 3,
        pointBackgroundColor: "rgba(0,0,255,1)",
        data: [],
      },
    ];

    for (const point of section.scatter) {
      let pos = 0;
      if (point.y - point.x > 0.3) {
        pos = 0;
      } else if (point.y - point.x < -0.3) {
        pos = 2;
      } else {
        pos = 1;
      }
      // @ts-ignore
      datasets[pos].data.push(point);
    }

    return (
      <div className="z-box" key={"spread_" + section.id}>
        <div className="z-title3">Spread - self-assessment vs feedback</div>
        <div className="z-body2">
          Each point is a person: self-assessment on the y-axis, feedback from others on the x-axis.
          Points on the line shows agreement.
        </div>
        <div className="mt-2 flex items-center z-body2">
          <div className="bg-amber-500 h-3 w-3 rounded-full"/>
          <div className="ml-1">{datasets[0].data.length} Rates them selves higher than other</div>
          <div className="ml-4 bg-gray-500 h-3 w-3 rounded-full"/>
          <div className="ml-1">{datasets[1].data.length} Agreement</div>
          <div className="ml-4 bg-blue-500 h-3 w-3 rounded-full"/>
          <div className="ml-1">{datasets[2].data.length} Rates them selves lower than others</div>
        </div>
        <div style={{width: 500}} className="mt-2">
          <Scatter
            data={{
              datasets: datasets,
            }}
            options={{
              aspectRatio: 1,
              indexAxis: 'y',
              responsive: true,
              scales: {
                x: { min: 0, max: answerOptions, title: {display: true, text: "Feedback"} },
                y:  { min: 0, max: answerOptions, title: {display: true, text: "Self-assessment"} }
              },
              plugins: {
                legend: { display: false },
                annotation: {
                  annotations: {
                    agreement: {
                      type: 'line',
                      xMin: 0,
                      xMax: answerOptions,
                      yMin: 0,
                      yMax: answerOptions,
                      borderColor: '#bbbbbb',
                      borderWidth: 2,
                      borderDash: [5, 5],
                      label: {display: false}
                    },
                  }
                },
              },
            }}
          />
        </div>
        <div className="z-title1 mt-4">Individual questions</div>
        {
          section.questions.map(question => {
            const selfColor = getScoreColor(question.scoreSelf, answerOptions);
            const feedbackColor = getScoreColor(question.scoreOther, answerOptions);
            return (
              <div key={"question_" + question.id} className="mt-4">
                <div className="z-body2">{question.question}</div>
                <div className="flex justify-between items-center">
                  <div className="z-body2 w-20">Self</div>
                  <ValueBar color={selfColor} value={question.scoreSelf} max={answerOptions}/>
                  <div className="z-body2 w-10 text-center">{question.scoreSelf.toFixed(1)}</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="z-body2 w-20">Feedback</div>
                  <ValueBar color={feedbackColor} value={question.scoreOther} max={answerOptions}/>
                  <div className="z-body2 w-10 text-center">{question.scoreOther.toFixed(1)}</div>
                </div>
              </div>
            )
          })
        }
      </div>
    )
  }

  const renderAssessment = (survey: SurveyInterface) => {
    if (survey.assessment.sections.length === 0) {
      return null;
    }
    let zoneLimits = [3, 6];
    if (survey.answerOptions === 5) {
      zoneLimits = [3, 4];
    }
    const zoneNr = [0, 0, 0];
    for (let section of survey.assessment.sections) {
      if (section.scoreOther < zoneLimits[0]) {
        zoneNr[0] += 1;
      } else if (section.scoreOther < zoneLimits[1]) {
        zoneNr[1] += 1;
      } else {
        zoneNr[2] += 1;
      }
    }

    return (
      <div className="mt-4">
        <div className="z-h3">{survey.name}</div>
        <div className="z-body2 mt-1">{survey.assessment.sections[0].clientsResponded} answers</div>

        <div className="flex gap-x-4 mt-8">
          <div className="z-box w-1/3">
            <div className="z-title4 text-gray-800!">Avg. Self-assessment</div>
            <div className="z-h1">{survey.assessment.scoreSelf.toFixed(1)}</div>
            <div className="z-body1 text-gray-800!">Across all areas - Scale 1-{survey.answerOptions}</div>
          </div>
          <div className="z-box w-1/3">
            <div className="z-title4 text-gray-800!">Avg. Feedback</div>
            <div className="z-h1">{survey.assessment.scoreOther.toFixed(1)}</div>
            <div className="z-body1 text-gray-800!">Across all areas - Scale 1-{survey.answerOptions}</div>
          </div>
          <div className="z-box w-1/3">
            <div className="z-title4 text-gray-800!">Response scale</div>
            <div className="body1 text-indigo-1000! mt-4">
              {survey.answerOptions}-point scale
            </div>

            <div className="mt-1 z-title3 text-cherry-600!">Critical: &lt; {zoneLimits[0]}</div>
            <div className="z-title3 text-orange-500!">Risk: {zoneLimits[0]} - {zoneLimits[1]}</div>
            <div className="z-title3 text-aquamarine-600!">Strength: &gt; {zoneLimits[1]}</div>

          </div>
        </div>

        <div className="mt-4">
          <PercentBar values={zoneNr} texts={[" Critical", " Risk", " Strength"]} showZero={false} goodLeft={false}
                      height="32px" width="100%"/>
        </div>

        <div className="z-h3 mt-8">Per area</div>

        <div className="mt-8 flex justify-between border-b border-neutral-300 pb-2">
          <div className="z-title3 text-gray-800!">Area</div>
          <div className="flex">
            <div className="z-title3 w-30 text-center text-gray-800!">Self-assessment</div>
            <div className="z-title3 w-30 text-center text-gray-800!">Feedback</div>
            <div className="z-title3 w-16 text-center text-gray-800!">Zone</div>
            <div className="w-12"/>
          </div>
        </div>
        {
          survey.assessment.sections.map(section => {
            const isOpened = openedSections.indexOf(section.id) > -1;
            return (
              <div key={"section_" + section.id} className="border-b border-neutral-300">
                <div className="flex justify-between items-center cursor-pointer h-10"
                     onClick={() => toggleSection(section.id, isOpened)}>
                  <div className="z-title3">{section.name}</div>
                  <div className="flex items-center">
                    <div
                      className={"z-title3 w-30 text-center text-" + getScoreColor(section.scoreSelf, survey.answerOptions) + "!"}>
                      {section.scoreSelf.toFixed(1)}
                    </div>
                    <div
                      className={"z-title3 w-30 text-center text-" + getScoreColor(section.scoreOther, survey.answerOptions) + "!"}>
                      {section.scoreOther.toFixed(1)}
                    </div>
                    <div className="w-16 text-center">
                      <RiskBadge score={section.scoreOther} zoneLimits={zoneLimits}/></div>
                    <div className="w-12 flex justify-end">
                      <ChevronDown className={"ml-3 h-4 w-4 " + (isOpened ? "rotate-180" : "")}/>
                    </div>

                  </div>
                </div>

                {isOpened && renderSpread(section, survey.answerOptions)}
              </div>
            )
          })
        }

        <div className="z-h3 mt-8">Per question</div>

        <div className="mt-8 flex justify-between border-b border-neutral-300 pb-2">
          <div className="z-title3 text-gray-800!">Question</div>
          <div className="flex">
            <div className="z-title3 w-50 text-gray-800!">Area</div>
            <div className="z-title3 w-30 text-center text-gray-800!">Self-assessment</div>
            <div className="z-title3 w-30 text-center text-gray-800!">Feedback</div>
            <div className="z-title3 w-16 text-center text-gray-800!">Zone</div>
          </div>
        </div>
        {
          survey.assessment.sections.map(section => {
            return (
              <div key={"section2_" + section.id}>
                {
                  section.questions.map(question => (
                    <div key={"question2_" + question.id}
                         className="flex justify-between items-center cursor-pointer h-10">
                      <div className="z-title3">{question.question}</div>
                      <div className="flex items-center">
                        <div className="z-title3 w-50">{section.name}</div>
                        <div
                          className={"z-title3 w-30 text-center text-" + getScoreColor(question.scoreSelf, survey.answerOptions) + "!"}>
                          {question.scoreSelf.toFixed(1)}
                        </div>
                        <div
                          className={"z-title3 w-30 text-center text-" + getScoreColor(question.scoreOther, survey.answerOptions) + "!"}>
                          {question.scoreOther.toFixed(1)}
                        </div>
                        <div className="w-16 text-center">
                          <RiskBadge score={question.scoreOther} zoneLimits={zoneLimits}/></div>
                      </div>
                    </div>
                  ))
                }
              </div>
            )
          })
        }
      </div>
    )
  }

  if (!loaded) {
    return <div>Loading data...</div>
  }
  if (error.length > 0) {
    return <div>{error}</div>
  }
  if (tooFewClients !== undefined) {
    return <TooFewClients tooFewClients={tooFewClients}/>
  }

  return (
    <>
      <div className="z-h2">The current state of your strategic initiative</div>
      <div className="z-body1 mt-4 text-gray-1000!">
        How the initiative itself is assessed: where it is strong, where there are gaps, and how aligned or
        scattered the answers are across units.
      </div>
      {
        surveys.map(survey => renderSurvey(survey))
      }
    </>
  )
}